import { getSections, deriveMeasurements } from '../../services/products';
import { getProductMeasurementSettings } from '../products/productsHelpers';
import { State } from '../StateTypes';
import {
  ColorObject,
  Item,
  Itemid,
  Itemids,
  TacItem,
} from '../../generalTypes';
import {
  ColorCombination,
  ColorCombinations,
  Filter,
  ProductMenuItem,
  SubfilterOption,
  Swiper,
} from './productMenuTypes';
import { selectRelatedIdsById } from '../products/productsSelectors';
import _ from 'lodash';
import { selectPresentTac } from '../tac/tacReducer/tacSelectors';
import tacHelpers from '../tac/tacHelpers';
import { rebasedItems } from '../../services/products/models';
import productService from '../../services/products';
import SwiperService, { getRange } from '../../services/swiper';
import { selectRawItems } from '../rawData/rawDataSelectors';
import {
  selectIsWallResizerActive,
  selectShowSceneSheet,
} from '../scene/sceneSelectors';
import { createSelector } from 'reselect';
import store from '../';
import { selectRangeData } from '../rangeData/rangeDataSelectors';
import { selectTac } from '../tac/tacSelectors';
import { TacModel } from '../tac/tacTypes';
import {
  FilterFunction,
  FilterObject,
} from '../../services/products/productsServiceTypes';

/**
 * Select sections sizes
 *
 * @returns {{depth: *[], width: *[], height: *[]}}
 */
export const selectSectionSizes = () => deriveMeasurements(getSections());

/**
 * Selects product menu
 *
 * @param productMenu
 * @returns {*}
 */
export const selectProductMenu = ({ productMenu }: State) => productMenu;

/**
 * Selects filters
 *
 * @param state
 * @returns {*}
 */
export const selectFiltersAsArray = (state: State): Filter[] =>
  Object.values(selectProductMenu(state).filters);

export const selectFiltersWithColorOptions = (state: State) =>
  selectFiltersAsArray(state).filter(({ colors }) => !!colors);

/**
 * Selects swiper
 *
 * @param state
 * @returns {*}
 */
export const selectSwiper = (state: State): Swiper =>
  selectProductMenu(state).swiper;

/**
 * Returns a given filter
 *
 * @param state {object}
 * @param filter {string}
 * @returns {*}
 */
export const selectFilter = (state: State, filter: string) =>
  selectProductMenuFilters(state)[filter];

/**
 * Select current filter selectable colors
 *
 * @param state
 */
export const selectCurrentFilterSelectableColors = (state: State) =>
  selectProductMenuFilter(state).selectableColors;

/**
 * Select current color filter
 *
 * @param state
 * @returns {{}}
 */
export const selectCurrentColorFilter = (state: State) => {
  return selectCurrentFilterSelectableColors(state)?.[
    selectProductMenuFilter(state).currentColorFilter
  ];
};

export const selectCurrentColorFilterName = (state: State) =>
  selectProductMenuFilter(state).currentColorFilter;

/**
 * Selects current sub filter
 *
 * @param state
 * @returns {Requireable<any>}
 */
export const selectCurrentSubFilterIndex = (state: State) =>
  selectSwiper(state).currentSubFilter;

export const selectDefaultSubFilterIndex = (state: State) => {
  const filters = selectProductMenuFilters(state);
  const relevantFilter = Object.values(filters).find(
    filter => filter.subFilter?.defaultSubFilter
  );
  return relevantFilter?.subFilter?.defaultSubFilter || 0;
};

/**
 * Select current sub filter value
 * @param state
 */
export const selectCurrentSubFilterValue = (state: State) => {
  return selectProductMenuFilter(state).subFilter?.options?.[
    selectCurrentSubFilterIndex(state)
  ]?.value;
};

/**
 * Select current sub filter key
 * @param state
 */
export const selectCurrentSubFilterKey = (state: State) =>
  selectProductMenuFilter(state).subFilter?.key;

/**
 * Select sub filter options
 * @param state
 */
export const selectCurrentSubFilterOptions = (state: State) =>
  selectProductMenuFilter(state).subFilter.options || [];

/**
 * Select sub filter options based on filter
 * @param filter
 */
export const selectSubFilterOptionsBasedOnFilter = (filter: Filter) =>
  filter.subFilter.options || [];

/**
 * Select all sub filter options
 * @param state
 */
export const selectAllSubFilterOptions = (state: State) => {
  return selectFiltersAsArray(state).reduce(
    (acc: Array<SubfilterOption>, filter: Filter) => [
      ...acc,
      ...selectSubFilterOptionsBasedOnFilter(filter),
    ],
    []
  );
};

/**
 * Returns formatted and ready to render measurements string
 *
 * @param state {object}
 * @param id {string}
 * @returns {*}
 */
export const selectMeasurementString = (
  state: State,
  id: Itemid
): string | undefined => selectRelatedIdsById(state, id)?.measurementString;

/**
 * Returns boolean for whether product measurements display
 * should override settings
 *
 * @param state {object}
 * @param id {string}
 * @returns {boolean}
 */
export const selectForceDisplayMeasurement = (
  state: State,
  id: Itemid
): boolean => !!selectRelatedIdsById(state, id)?.forceDisplay;

/**
 * Returns boolean according to settings if a measurement
 * should be displayed for product
 *
 * @param state {object}
 * @param product {object}
 * @returns {boolean|*}
 */
export const selectShouldDisplayMeasurements = (
  state: State,
  product: TacItem
) =>
  selectForceDisplayMeasurement(state, product?.id) ||
  getProductMeasurementSettings(product.filter.type)?.display ||
  false;

/**
 * Select product menu filters
 *
 * @param state
 */
export const selectProductMenuFilters = (state: State) =>
  selectProductMenu(state).filters;

/**
 * Returns current product menu filter
 *
 * @param state
 * @returns {*}
 */
export const selectProductMenuFilter = (state: State) => {
  const currentFilter = selectSwiper(state)?.currentFilter;

  if (currentFilter) {
    return selectProductMenuFilters(state)[currentFilter];
  }
  return Object.values(selectProductMenuFilters(state))[0];
};

/**
 * Select filter items
 */
export const selectFilterItems = (state: State, filterName: string) => {
  const filter = selectFilter(state, filterName);
  return filter.products.flat().reduce<ProductMenuItem[]>((acc, id) => {
    const item = productService.getProduct(id);
    return item ? [...acc, item] : acc;
  }, []);
};

/**
 * Returns swiper layout
 * @param state
 * @returns {*}
 */
export const selectProductMenuLayout = (state: State) =>
  selectProductMenuFilter(state).swiperLayout.name;

/**
 * Select product menu item
 * @param product
 * @param tac
 */
export const selectProductMenuItem = (product: TacItem, tac: TacModel) => {
  const rangeApi = getRange();

  const { space, rebased } = tac
    ? { space: tacHelpers.getSpace(tac), rebased: rebasedItems(tac.items) }
    : { space: undefined, rebased: undefined };

  /**
   * Get disabled status
   * @returns {*|boolean}
   */
  const disabledStatus = () =>
    tac
      ? rangeApi.hasOwnProperty('getDisabledStatus')
        ? rangeApi.getDisabledStatus(tac, product, space, rebased)
        : SwiperService.getDisabledStatus(tac, product, space, rebased)
      : '...';

  /**
   * Create items object
   * @param product
   */
  return {
    product,
    disabled: disabledStatus(),
  };
};

/**
 * Has selectable colors
 * @param state
 */
export const selectFilterHasSelectableColors = (state: State) =>
  selectSelectableColorsAsArray(state)?.length > 1;

/**
 * Sub filter has items
 * @param state
 * @param filterName
 * @param filterOption
 */
export const selectSubFilterHasItems = (
  state: State,
  filterName: string,
  filterOption: any
) => {
  const filter = selectFilter(state, filterName);
  const subFilterKey = filter.subFilter.key;
  const rawItems = selectRawItems(state);
  const items = filter.products.reduce<TacItem[]>((acc: TacItem[], [id]) => {
    if (!id) return acc;

    return id && productService.getProduct(id) ? [...acc, rawItems[id]] : acc;
  }, []);

  return !!items.filter(product => {
    // @ts-ignore LIMITATION OF ANCIENT, PRE-HISTORIC TS-VERSION NOT SUPPORTING ENUMS
    return product[subFilterKey] === filterOption.value;
  }).length;
};

/**
 * Select filtered swiper items
 */
export const selectFilteredSwiperItems = createSelector(
  [
    selectPresentTac,
    selectProductMenuFilter,
    selectIsWallResizerActive,
    selectRawItems,
    selectRangeData,
    selectSwiper,
    selectShowSceneSheet,
  ],
  (tac, productMenuFilter, wallResizerActive, rawItems, rangedData, swiper) => {
    /**
     * Runs filters on items, including range specific filters
     */
    const runFilters = (
      filteredItems: ProductMenuItem[],
      filter: FilterFunction
    ) => filteredItems.filter(filter(store.getState()));

    /**
     * Returns products
     * @param itemsIds
     */
    const selectProducts = (itemsIds: Itemids) =>
      itemsIds.reduce<Item[]>(
        (acc, id: Itemid) =>
          id && productService.getProduct(id)
            ? [...acc, selectProductMenuItem(rawItems[id], tac)]
            : acc,
        []
      );

    /**
     * Returns filtered items in sorted array
     * @param itemsIds
     */
    const handleItemsCluster = (itemsIds: Itemids) =>
      productService
        .getFilters()
        .map(({ filter }: FilterObject) => filter)
        .reduce(runFilters, selectProducts(itemsIds));

    return productMenuFilter.products.reduce<ProductMenuItem[][]>(
      (acc, ids) => {
        if (!ids.length) return acc;

        const products = handleItemsCluster(ids);
        return products.length ? [...acc, products] : acc;
      },
      []
    );
  },
  {
    memoizeOptions: {
      equalityCheck: (a, b) =>
        selectIsWallResizerActive(store.getState()) ? true : a === b,
      maxSize: 1,
    },
  }
);

/**
 * Select name of current filter
 * @param state
 */
export const selectCurrentFilterName = (state: State) =>
  selectSwiper(state).currentFilter;

/**
 * Select unique colors
 * @param state
 * @param filterName
 * @returns {*[]}
 */
export const selectUniqueColors = (state: State, filterName: string) => {
  const rawItems = selectRawItems(state);
  const tac = selectTac(state);
  const filter = selectFilter(state, filterName);

  if (!tac) return [];

  const items = filter.products.reduce<ProductMenuItem[]>(
    (acc: ProductMenuItem[], [id]) => {
      const product = rawItems[id];
      if (id && productService.getProduct(id))
        return [...acc, selectProductMenuItem(product, tac)];

      return acc;
    },
    []
  );

  const colorCanBeAdded = (
    colorArr: string[],
    ProductSwiperItem: ProductMenuItem
  ) => {
    const color = ProductSwiperItem?.product?.filter?.color;
    return filter?.colors?.[color] && !colorArr.includes(color);
  };

  return items.reduce(
    (acc: string[], productSwiperItem: ProductMenuItem) =>
      colorCanBeAdded(acc, productSwiperItem)
        ? [...acc, productSwiperItem.product.filter.color]
        : acc,
    []
  );
};

/**
 * Select filter text key
 * @param state
 */
export const selectFilterText = (state: State) =>
  selectProductMenuFilter(state).filterTextKeys;

/**
 * Select is selected filter
 * @param filter
 * @returns {boolean}
 */
export const selectIsSelectedFilter = (filter: Filter) => (state: State) =>
  selectProductMenuFilter(state).name === filter.name;

/**
 * Select available colors
 * @param state
 * @returns {null|*[]|*[]}
 */
export const selectAvailableColors = (state: State) =>
  selectProductMenuFilter(state).selectableColors;

/**
 * Select selectable colors
 * @param state
 * @param filterName
 */
export const selectSelectableColors = (
  state: State,
  filterName: string = selectCurrentFilterName(state)
) => {
  return selectFilter(state, filterName)?.selectableColors;
};

/**
 * Select selectable colors as array
 * @param state
 * @param filterName
 */
export const selectSelectableColorsAsArray = (
  state: State,
  filterName: string = selectCurrentFilterName(state)
) => {
  const selectableColorsArray = selectSelectableColors(state, filterName);
  return selectableColorsArray && Object.values(selectableColorsArray);
};

/**
 * Select selected color for filter
 * @param state
 * @param filterName
 */
export const selectFilterSelectedColor = (state: State, filterName: string) => {
  return selectSelectableColors(state)?.[
    selectFilter(state, filterName).currentColorFilter
  ];
};

/**
 * Select combined available colors
 * @param state
 * @param filterName
 * @returns {unknown[] | any[]}
 */
export const selectCombinedAvailableColors = (
  state: State,
  filterName: string
) => {
  type Keeps = { name: string; colors: string[] };
  type Discards = string;

  type sorterObject = {
    keeps: Keeps[];
    discards: Discards[];
  };

  const uniqueColors = selectUniqueColors(state, filterName);
  const filter = selectFilter(state, filterName);

  /**
   * Return combinable colors
   * @param combinables
   */
  const getCombinableColors = (combinables: string[]) =>
    combinables.filter(combinable => uniqueColors.includes(combinable));

  /**
   * Builds a color object
   * @param colors
   */
  const generateColorObject = (
    colors: string[],
    silentlyIncludeColors: string[] = []
  ) => ({
    name: colors.sort().join('_'),
    colors: [...colors, ...silentlyIncludeColors],
  });

  /**
   * Calculates color object
   * @param discards
   * @param currentColor
   */
  const calculateColorObject = (
    discards: Discards[],
    currentColor: ColorObject
  ) => {
    if (discards.includes(currentColor.value)) return [];

    const combinableColors = getCombinableColors(currentColor.combinables);
    const silentlyIncludeColors = currentColor.silentlyInclude;

    return combinableColors.length
      ? combinableColors.map((combinable: string) =>
          generateColorObject([currentColor.value, combinable])
        )
      : [generateColorObject([currentColor.value], silentlyIncludeColors)];
  };

  /**
   * Filters what to colors to keep and which to discard
   */
  const filterUniqueColors = () => {
    return uniqueColors.reduce<sorterObject>(
      (acc: sorterObject, curr: string) => {
        const currentColor = filter.colors[curr];
        return {
          keeps: [
            ...acc.keeps,
            ...calculateColorObject(acc.discards, currentColor),
          ],
          discards: [...acc.discards, ...currentColor.combinables],
        };
      },
      { keeps: [], discards: [] }
    ).keeps;
  };

  /**
   * Sort colors
   * @param aColor
   * @param bColor
   */
  const sortColors = (
    { colors: [aColor] }: ColorCombination,
    { colors: [bColor] }: ColorCombination
  ) => _.clamp(filter.colors[aColor].sort - filter.colors[bColor].sort, -1, 1);

  return filterUniqueColors()
    .sort(sortColors)
    .reduce(
      (acc: ColorCombinations, curr: ColorCombination) => ({
        ...acc,
        [curr.name]: curr,
      }),
      {}
    );
};
